import { ApiProvider } from '@/provider/ApiProvider';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import LoginIcon from '@mui/icons-material/Login';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { setHeaderFilters } from '@/selection/headerSelection';

function SavedFiltersPopUp() {
  const { updateGlobalFilters } = useGlobalFilters();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [defaultFilterId, setDefaultFilterId] = useState(null);
  const [viewFilterOpen, setViewFilterOpen] = useState(false);
  const [currentFilterCombination, setCurrentFilterCombination] = useState('');

  const GetFilters = () => {
    setLoading(true);
    ApiProvider.webserviceGet(`Dashboard/Filters/GetFilters?page=${page}`).then(
      (response) => {
        if (response.success == true) {
          const defaultItem = response.data.original.data.find((item) => item.is_default === 1);
          if (defaultItem) {
            setDefaultFilterId(defaultItem.id);
          }
          setData(response.data.original.data);
          setTotalRows(response.data.original.total);
        } else {
          setData([]);
        }
        setLoading(false);
      },
      (error) => {
        setData([]);
        setLoading(false);
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  };

  useEffect(() => {
    if (open === true) {
      GetFilters();
    }
  }, []);

  useEffect(() => {
    if (open === true) {
      GetFilters();
    }
  }, [page]);

  const handleDefaultFilterChange = (id) => {
    setDefaultFilterId(id);
    let body = new FormData();
    body.append('filter_id', id);

    ApiProvider.webservicePostToken('Dashboard/Filters/ApplyDefaultFilter', body, 'yes').then(
      (response) => {
        if (response.success == true) {
          GetFilters();
          ApiProvider.globalToastMessage(response.message, 2000, 'success');
        } else {
          ApiProvider.globalToastMessage(response.message, 5000, 'error');
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  };

  const handleApplyFilter = (id) => {
    const filterarrayValue = data.find((item) => item.id === id);
    setHeaderFilters(filterarrayValue.filters_comination);
    try {
      const parsed = JSON.parse(filterarrayValue.filters_comination);
      updateGlobalFilters(Array.isArray(parsed) ? parsed : []);
    } catch {
      updateGlobalFilters([]);
    }
    setOpen(false);
  };

  const handleViewFilter = (id) => {
    const filterItem = data.find((item) => item.id === id);
    const filterArray = {
      combination: JSON.parse(filterItem.filters_comination),
      name: filterItem.filters_comination_name,
    };
    setCurrentFilterCombination(filterArray);
    setViewFilterOpen(true);
  };

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const promptDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = (id) => {
    if (!id) return;
    let body = new FormData();
    body.append('filter_id', id);

    ApiProvider.webservicePostToken('Dashboard/Filters/DeleteFilters', body, 'yes').then(
      (response) => {
        if (response.success == true) {
          GetFilters();
          ApiProvider.globalToastMessage(response.message, 2000, 'success');
        } else {
          ApiProvider.globalToastMessage(response.message, 5000, 'error');
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  };

  return (
    <div>
      <Button
        variant='contained'
        color='primary'
        onClick={() => {
          GetFilters();
          setOpen(true);
        }}
        sx={{
          '&.MuiButton-root': {
            fontWeight: '500',
            textTransform: 'initial',
            boxShadow: 'none',
          },
        }}
      >
        <FileUploadIcon sx={{ mr: '8px' }} />
        Load filter
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Saved Filters</DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress color='primary' />
            </div>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Default Filter</TableCell>
                    <TableCell>Show/Apply/Delete Filter</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(data) &&
                    data.length > 0 &&
                    data.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>{item.filters_comination_name}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={defaultFilterId === item.id}
                            onChange={() => handleDefaultFilterChange(item.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton color='secondary' onClick={() => handleViewFilter(item.id)}>
                            <FilterListIcon />
                          </IconButton>
                          <IconButton color='secondary' onClick={() => handleApplyFilter(item.id)}>
                            <LoginIcon />
                          </IconButton>
                          <IconButton color='secondary' onClick={() => promptDelete(item.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                component='div'
                count={totalRows}
                page={page - 1}
                onPageChange={(event, newPage) => setPage(newPage + 1)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                labelRowsPerPage=''
              />
              <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Are you sure you want to delete this item?</DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmOpen(false)} color='primary'>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      handleDelete(deleteId);
                      setConfirmOpen(false);
                    }}
                    color='secondary'
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={viewFilterOpen}
                onClose={() => setViewFilterOpen(false)}
                maxWidth='sm'
                fullWidth
              >
                <DialogTitle>
                  Filter Combination - <b>{currentFilterCombination.name}</b>
                </DialogTitle>
                <DialogContent>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Filter Type</TableCell>
                        <TableCell>Filter Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(currentFilterCombination.combination) &&
                        currentFilterCombination.combination.map((filter, index) => (
                          <TableRow key={index}>
                            <TableCell>{capitalizeFirstLetter(filter.filter_type)}</TableCell>
                            <TableCell>
                              {filter.name ||
                                filter.full_name ||
                                filter.category_name ||
                                filter.currency_name ||
                                'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setViewFilterOpen(false)} color='primary'>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SavedFiltersPopUp;
